/**
 * useErrorHandler Hook
 *
 * Custom hook for handling errors in functional components
 * Provides consistent error handling with user-friendly messages
 */

import { useState, useCallback } from 'react';
import { normalizeError, getUserFriendlyMessage, logError } from '../utils/error-handler';
import type { AppError } from '../types/errors';

interface UseErrorHandlerReturn {
  error: AppError | null;
  setError: (error: unknown) => void;
  clearError: () => void;
  handleError: (error: unknown, context?: Record<string, any>) => void;
  errorMessage: string | null;
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setErrorState] = useState<AppError | null>(null);

  const setError = useCallback((error: unknown) => {
    const normalized = normalizeError(error);
    setErrorState(normalized);
    logError(normalized);
  }, []);

  const clearError = useCallback(() => {
    setErrorState(null);
  }, []);

  const handleError = useCallback((error: unknown, context?: Record<string, any>) => {
    const normalized = normalizeError(error);
    setErrorState(normalized);
    logError(normalized, context);
  }, []);

  const errorMessage = error ? getUserFriendlyMessage(error) : null;

  return {
    error,
    setError,
    clearError,
    handleError,
    errorMessage,
  };
}
